import { format } from "date-fns";
import { useStateParams } from "../hooks/useStateParams";
import { ApiPaginatedData } from "./common/paginatedData";
import useAuthenticatedFetch from "./common/useAuthenticatedFetch";
import { ApiResponse } from "./models/ApiResponse";

export type SectorReport = {
    name: string;
    id: string;
    scopeOneAndTwoFinancedEmissions: number;
    scopeOneAndTwoWeightedPCAFScore: number;
    scopeThreeFinancedEmissions: number;
    scopeThreeWeightedPCAFScore: number;
    financedEmissionsIntensity: number;
};

export type usePaginatedSectorReportReturn = {
    sectorReports: SectorReport[];
    pageNumber: number;
    pageSize: number;
    year: number;
    isLoading: boolean;
    setPageNumber: (pageNumber: number) => void;
    setPageSize: (pageSize: number) => void;
    setYear: (year: number) => void;
};

export const defaultPageIndex = 0;
export const defaultPageSize = 15;
export const maxPageSize = 100;
export const defaultYear = 2025;

export const usePaginatedSectorReport: () => usePaginatedSectorReportReturn =
    () => {
        const [pageNumber, setPageNumber] = useStateParams<number>(
            defaultPageIndex,
            "page",
            (s) => s.toString(),
            (s) => (!isNaN(parseInt(s)) ? parseInt(s) : defaultPageIndex)
        );
        const [pageSize, setPageSize] = useStateParams<number>(
            defaultPageSize,
            "pageSize",
            (s) => s.toString(),
            (s) => (!isNaN(parseInt(s)) ? parseInt(s) : defaultPageSize)
        );
        const [year, setYear] = useStateParams<number>(
            defaultYear,
            "year",
            (s) => s.toString(),
            (s) => (!isNaN(parseInt(s)) ? parseInt(s) : defaultYear)
        );

        const startDate = new Date();
        const endDate = new Date();

        startDate.setFullYear(year, 0, 1);
        startDate.setUTCHours(0);
        startDate.setUTCMinutes(0);
        startDate.setUTCSeconds(0);
        startDate.setMilliseconds(0);

        endDate.setFullYear(year + 1, 0, 1);
        endDate.setUTCHours(0);
        endDate.setUTCMinutes(0);
        endDate.setUTCSeconds(0);
        endDate.setMilliseconds(0);

        const { response, isLoading } = useAuthenticatedFetch<
            ApiResponse<ApiPaginatedData<SectorReport>>
        >(
            `api/reports/financed-emissions?pageIndex=${pageNumber}` +
                `&pageSize=${pageSize}` +
                `&startDate=${format(startDate, "yyyy-MM-dd")}` +
                `&endDate=${format(endDate, "yyyy-MM-dd")}`
        );

        return {
            pageNumber,
            pageSize,
            year,
            sectorReports: response?.content.items ?? [],
            isLoading,
            setPageNumber,
            setPageSize,
            setYear,
        };
    };

export type useSectorFinancedEmissionsOriginReturn = {
    sectorFinancedEmissionsOrigin: SectorFinancedEmissionsOriginDto | undefined;
    year: number;
    isLoading: boolean;
    scopes: number[];
    setYear: (year: number) => void;
    setScopes: (scopes: number[]) => void;
};

export type SectorFinancedEmissionsOriginDto = {
    sectorName: string;
    totalFinancedEmissions: number;
    assetOrigins: AssetFinancedEmissionsOriginDto[];
};

export type AssetFinancedEmissionsOriginDto = {
    financedEmissions: number;
    outstandingAmount: number;
    enterpriseValue: number;
    totalC02e: number;
    assetId: number;
    assetName: string;
    dataSourceId: number;
    evidenceIds: number[];
};

export const useSectorFinancedEmissionsOrigin = (
    sectorId: string,
    initialYear: string,
    initialScopes: string
): useSectorFinancedEmissionsOriginReturn => {
    const [year, setYear] = useStateParams<number>(
        Number(initialYear),
        "year",
        (s) => s.toString(),
        (s) => (!isNaN(parseInt(s)) ? parseInt(s) : defaultYear)
    );

    const [scopes, setScopes] = useStateParams<number[]>(
        initialScopes.split(",").map(Number),
        "scopes",
        (s) => s.join(","),
        (s) =>
            Array.isArray(s.split(","))
                ? s.split(",").map((v) => parseInt(v))
                : [parseInt(s)]
    );

    const scopesQueryParam = scopes.length > 0 ? `&scopes=${scopes}` : "";

    const startDate = new Date();
    const endDate = new Date();

    startDate.setFullYear(year, 0, 1);
    startDate.setUTCHours(0);
    startDate.setUTCMinutes(0);
    startDate.setUTCSeconds(0);
    startDate.setMilliseconds(0);

    endDate.setFullYear(year + 1, 0, 1);
    endDate.setUTCHours(0);
    endDate.setUTCMinutes(0);
    endDate.setUTCSeconds(0);
    endDate.setMilliseconds(0);

    const { response, isLoading } = useAuthenticatedFetch<
        ApiResponse<SectorFinancedEmissionsOriginDto>
    >(
        `api/sectors/${sectorId}/financed-emissions/origin?` +
            `&startDate=${format(startDate, "yyyy-MM-dd")}` +
            `&endDate=${format(endDate, "yyyy-MM-dd")}` +
            `${scopesQueryParam}`
    );

    return {
        year,
        sectorFinancedEmissionsOrigin: response?.content,
        isLoading,
        scopes,
        setYear,
        setScopes,
    };
};

export type FinancedEmissionsHistoryDto = {
    sectorName: string;
    dataPoints: FinancedEmissionsHistoryDataPointDto[];
};

export type FinancedEmissionsHistoryDataPointDto = {
    year: number;
    financedEmissions: number;
};

export type useFinancedEmissionsHistoryReturn = {
    isLoading: boolean;
    financedEmissionsHistory?: FinancedEmissionsHistoryDto;
    year: number;
    scopes: number[];
    setYear: (year: number) => void;
    setScopes: (scopes: number[]) => void;
};

export const useFinancedEmissionsHistory = (
    sectorId: string,
    initialYear: string,
    initialScopes: string
): useFinancedEmissionsHistoryReturn => {
    const [year, setYear] = useStateParams<number>(
        Number(initialYear),
        "year",
        (s) => s.toString(),
        (s) => (!isNaN(parseInt(s)) ? parseInt(s) : defaultYear)
    );
    
    const [scopes, setScopes] = useStateParams<number[]>(
        initialScopes.split(",").map(Number),
        "scopes",
        (s) => s.join(","),
        (s) =>
            Array.isArray(s.split(","))
                ? s.split(",").map((v) => parseInt(v))
                : [parseInt(s)]
    );

    const scopesQueryParam = scopes.length > 0 ? `&scopes=${scopes}` : "";

    const { response, isLoading } = useAuthenticatedFetch<
        ApiResponse<FinancedEmissionsHistoryDto>
    >(
        `api/sectors/${sectorId}/financed-emissions/history?` +
            `&year=${year}` +
            `${scopesQueryParam}`
    );

    return {
        isLoading,
        scopes,
        year,
        financedEmissionsHistory: response?.content,
        setScopes,
        setYear
    };
};
